import Phaser from "phaser";
import AudioManager from "../../../@shared/utils/AudioManager";
import northDocksPng from "../../../@shared/assets/GameMaps/NorthDocks/north-docks.png";
import {
  getAvatarSpriteConfig,
  scaleAvatarBodySize,
} from "../../../@shared/utils/SpritesMap";
import { createWalkAnimations } from "../../../@shared/utils/SceneHelpers";
import {
  createLocalOrbAvatarImage,
  getLocalOrbAvatarConfig,
  preloadLocalOrbAvatar,
  updateLocalOrbAvatar,
} from "../../../@shared/utils/phaserOrbAvatar";
import { applyWorldSceneBackground } from "../../../@shared/utils/worldSceneBackgrounds";

const DOOR_SFX = require("../../../@shared/assets/sounds/door.mp3");

const DEFAULT_SPAWN = { x: 480, y: 520 };
const DEFAULT_RETURN_SPAWN = { x: 1100, y: 560 };
const EXIT_SENSOR = {
  x: 480,
  y: 590,
  width: 176,
  height: 64,
};

export default class NorthDocksScene extends Phaser.Scene {
  constructor() {
    super({ key: "NorthDocksScene" });
  }

  init(data = {}) {
    this.spawnPoint = data.spawn || DEFAULT_SPAWN;
    this.returnSpawn = data.returnSpawn || DEFAULT_RETURN_SPAWN;
    this.heroUrl = data.heroUrl || null;
    this.heroAvatarKey = data.heroAvatarKey || "io_default";
    this.heroSpriteConfig = getAvatarSpriteConfig(this.heroAvatarKey);
    this.userData = data.userData || null;
    const { avatarData, textureKey } = getLocalOrbAvatarConfig(this.userData);
    this.localOrbAvatarData = avatarData;
    this.localOrbTextureKey = textureKey;
  }

  preload() {
    if (this.heroUrl && !this.textures.exists("hero")) {
      this.load.spritesheet("hero", this.heroUrl, {
        frameWidth: this.heroSpriteConfig.frameWidth,
        frameHeight: this.heroSpriteConfig.frameHeight,
      });
    }
    preloadLocalOrbAvatar(this, this.localOrbAvatarData, this.localOrbTextureKey);

    if (!this.textures.exists("northDocksBg")) {
      this.load.image("northDocksBg", northDocksPng);
    }

    AudioManager.loadSFX("door", DOOR_SFX);
  }

  create() {
    applyWorldSceneBackground(this);
    const bg = this.add.image(0, 0, "northDocksBg").setOrigin(0).setDepth(0);
    const worldWidth = bg.width;
    const worldHeight = bg.height;

    this.matter.world.setBounds(0, 0, worldWidth, worldHeight);
    this.cameras.main.setBounds(0, 0, worldWidth, worldHeight);

    this.player = this.matter.add
      .sprite(this.spawnPoint.x, this.spawnPoint.y, "hero", 0)
      .setBody(
        scaleAvatarBodySize(this.heroAvatarKey, { width: 45, height: 65 }),
      )
      .setScale(this.heroSpriteConfig.worldScale || 1)
      .setFixedRotation()
      .setDepth(this.spawnPoint.y + 24);
    this.playerOrbImage = createLocalOrbAvatarImage(
      this,
      this.player,
      this.spawnPoint,
      this.localOrbAvatarData,
      this.localOrbTextureKey,
      { depth: this.spawnPoint.y + 24 },
    );
    this.playerDebugGfx = this.heroSpriteConfig.showCollisionDebug
      ? this.add.graphics().setDepth(5002)
      : null;

    const isMobile =
      this.sys.game.device.os.android || this.sys.game.device.os.iOS;
    this.cameras.main
      .startFollow(this.player, true, 0.1, 0.1)
      .setZoom(isMobile ? 1.2 : 1.85);

    createWalkAnimations(this, "hero", this.heroAvatarKey);

    this.currentDoor = null;
    this.createReturnPortal();

    this.matter.world.on("collisionstart", (event) => {
      event.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (!sensor || !isPlayer || sensor.label !== "exit") return;

        this.currentDoor = "exit";
        window.dispatchEvent(
          new CustomEvent("interact-proximity", { detail: true }),
        );
        window.dispatchEvent(
          new CustomEvent("door-name", { detail: "Return To Void City" }),
        );
      });
    });

    this.matter.world.on("collisionend", (event) => {
      event.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (!sensor || !isPlayer || sensor.label !== this.currentDoor) return;

        this.currentDoor = null;
        window.dispatchEvent(
          new CustomEvent("interact-proximity", { detail: false }),
        );
        window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
      });
    });

    this.joystick = { x: 0, y: 0 };
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });

    this.spaceKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE,
    );
    this._onEnter = () => {
      if (this.currentDoor !== "exit") return;

      AudioManager.playSFX("door");
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once("camerafadeoutcomplete", () => {
        this.scene.stop(this.scene.key);
        this.scene.start("VoidCity2Scene", {
          spawn: this.returnSpawn,
          returnSpawn: this.returnSpawn,
          heroUrl: this.heroUrl,
          heroAvatarKey: this.heroAvatarKey,
          userData: this.userData,
        });
      });
    };

    this.spaceKey.on("down", this._onEnter);
    window.addEventListener("press-enter", this._onEnter);

    this.events.once("shutdown", () => {
      this.spaceKey?.off?.("down", this._onEnter);
      window.removeEventListener("press-enter", this._onEnter);
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
    });

    this.add
      .text(56, 48, "North Docks", {
        font: "28px Arial",
        fill: "#edf7ff",
        stroke: "#123448",
        strokeThickness: 6,
      })
      .setOrigin(0, 0.5)
      .setDepth(200)
      .setScrollFactor(0.15);

    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  createReturnPortal() {
    this.matter.add.rectangle(
      EXIT_SENSOR.x,
      EXIT_SENSOR.y,
      EXIT_SENSOR.width,
      EXIT_SENSOR.height,
      {
        isStatic: true,
        isSensor: true,
        label: "exit",
      },
    );

    const glow = this.add.graphics().setDepth(200);
    glow.fillStyle(0x67d7ff, 0.16);
    glow.fillEllipse(EXIT_SENSOR.x, 584, 184, 42);
    glow.fillStyle(0x9fe8ff, 0.28);
    glow.fillRoundedRect(EXIT_SENSOR.x - 48, 564, 96, 24, 12);
    glow.lineStyle(2, 0xe8fbff, 0.92);
    glow.strokeRoundedRect(EXIT_SENSOR.x - 48, 564, 96, 24, 12);

    this.add
      .text(EXIT_SENSOR.x, 540, "Return", {
        font: "16px Arial",
        fill: "#f0fbff",
        stroke: "#11384d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(201);
  }

  update() {
    const speed = 2;
    let vx = 0;
    let vy = 0;
    const { x: jx, y: jy } = this.joystick;

    if (jx || jy) {
      vx = jx * speed;
      vy = jy * speed;
    } else {
      if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
      else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
      if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
      else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
    }

    if (vx && vy) {
      vx *= Math.SQRT1_2;
      vy *= Math.SQRT1_2;
    }

    this.player.setVelocity(vx, vy);
    this.player.setDepth(this.player.y + 24);

    if (this.playerDebugGfx && this.player?.body?.bounds) {
      const bounds = this.player.body.bounds;
      const width = bounds.max.x - bounds.min.x;
      const height = bounds.max.y - bounds.min.y;

      this.playerDebugGfx.clear();
      this.playerDebugGfx.lineStyle(2, 0x7bf1a8, 0.95);
      this.playerDebugGfx.fillStyle(0x7bf1a8, 0.12);
      this.playerDebugGfx.strokeRect(bounds.min.x, bounds.min.y, width, height);
      this.playerDebugGfx.fillRect(bounds.min.x, bounds.min.y, width, height);
    }

    if (
      updateLocalOrbAvatar(
        this,
        this.player,
        this.playerOrbImage,
        this.localOrbAvatarData,
        {
          x: this.player.x,
          y: this.player.y,
          vx,
          depth: this.player.y + 24,
        },
      )
    ) {
      // Orb avatars use the image overlay instead of the walking spritesheet.
    } else if (Math.abs(vy) > Math.abs(vx)) {
      if (vy < 0) this.player.anims.play("walk-up", true);
      else if (vy > 0) this.player.anims.play("walk-down", true);
    } else if (vx < 0) {
      this.player.anims.play("walk-left", true);
    } else if (vx > 0) {
      this.player.anims.play("walk-right", true);
    } else {
      this.player.anims.stop();
    }
  }
}
